import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCcw, Trash2 } from 'lucide-react';
import ImageModal from './components/ImageModal';
import HistoryGroupCard from './components/history/HistoryGroupCard';
import HistoryPagination from './components/history/HistoryPagination';
import {
  getGroupImageUrls,
  getGroupKey,
  groupHistoryItems,
  normalizeHistoryImageKey,
} from './components/history/historyUtils';
import { enrichPriceEstimateFields } from './utils/formatters';
import { useAuth } from './context/AuthContext';
const HISTORY_BATCH_SIZE = 80;

const HistoryPage = () => {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [originalItemsById, setOriginalItemsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageUrls, setSelectedImageUrls] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [ebaySearchingGroup, setEbaySearchingGroup] = useState(null);
  const [ebayResultsMap, setEbayResultsMap] = useState({});
  const [disabledImages, setDisabledImages] = useState({});
  const [rerunningGroup, setRerunningGroup] = useState(null);
  const fetchItems = async ({ reset = false } = {}) => {
    const isIncrementalLoad = !reset && Boolean(historyCursor?.id);
    if (isIncrementalLoad) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = { limit: HISTORY_BATCH_SIZE };
      if (!reset && historyCursor?.id) {
        params.cursor_id = historyCursor.id;
      }
      const response = await axios.get('/get_items', { params });
      const payload = response.data;
      const fetchedItems = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.items) ? payload.items : []);
      const normalizedFetchedItems = fetchedItems.map((item) => (
        item && typeof item === 'object' ? enrichPriceEstimateFields(item) : item
      ));
      const paging = Array.isArray(payload) ? null : payload?.paging;
      setItems((prevItems) => {
        const existingIds = new Set((reset ? [] : prevItems).map((item) => item.id));
        const dedupedIncoming = normalizedFetchedItems.filter((item) => !existingIds.has(item.id));
        return reset ? dedupedIncoming : [...prevItems, ...dedupedIncoming];
      });
      setOriginalItemsById((prev) => {
        const next = reset ? {} : { ...prev };
        normalizedFetchedItems.forEach((item) => {
          if (!item || item.id === undefined || item.id === null) return;
          if (next[item.id] && !reset) return;
          next[item.id] = {
            actual_price: item.actual_price,
            manual_name: item.manual_name || '',
          };
        });
        return next;
      });
      setHasMoreHistory(Boolean(paging?.has_more));
      setHistoryCursor(paging?.next_cursor || null);
      if (reset) {
        setCurrentPage(1);
        setDisabledImages({});
        setEbayResultsMap({});
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      if (isIncrementalLoad) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };
  const refreshHistory = async () => {
    await fetchItems({ reset: true });
  };
  const flushHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all analysis history and blob storage? This cannot be undone.')) return;
    try {
      const response = await axios.post('/flush_history');
      if (response.data.status === 'success') {
        setItems([]);
        setOriginalItemsById({});
        setHasMoreHistory(false);
        setHistoryCursor(null);
        setDisabledImages({});
        setEbayResultsMap({});
        setCurrentPage(1);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error flushing history:', error);
      alert('Failed to flush history.');
    }
  };
  useEffect(() => {
    fetchItems({ reset: true });
  }, []);
  const toggleImage = (groupKey, imageUrl, allUrls = []) => {
    setDisabledImages((prev) => {
      const imageKey = normalizeHistoryImageKey(imageUrl);
      if (!imageKey) return prev;
      const allKeys = Array.from(new Set(
        (Array.isArray(allUrls) ? allUrls : [])
          .map((url) => normalizeHistoryImageKey(url))
          .filter(Boolean),
      ));
      const groupDisabled = new Set(
        (Array.isArray(prev[groupKey]) ? prev[groupKey] : [])
          .map((key) => normalizeHistoryImageKey(key))
          .filter(Boolean),
      );
      const isCurrentlyDisabled = groupDisabled.has(imageKey);
      if (isCurrentlyDisabled) {
        groupDisabled.delete(imageKey);
      } else {
        const enabledKeys = allKeys.filter((key) => !groupDisabled.has(key));
        const disablingLastEnabledImage = enabledKeys.length <= 1 && enabledKeys.includes(imageKey);
        if (disablingLastEnabledImage) {
          return prev;
        }
        groupDisabled.add(imageKey);
      }
      return { ...prev, [groupKey]: Array.from(groupDisabled) };
    });
  };
  const getEnabledUrls = (group) => {
    const groupKey = getGroupKey(group);
    const disabled = new Set(
      (Array.isArray(disabledImages[groupKey]) ? disabledImages[groupKey] : [])
        .map((key) => normalizeHistoryImageKey(key))
        .filter(Boolean),
    );
    const allUrls = getGroupImageUrls(group);
    const enabledUrls = [];
    const seenKeys = new Set();
    allUrls.forEach((url) => {
      const key = normalizeHistoryImageKey(url);
      if (!key || seenKeys.has(key) || disabled.has(key)) return;
      seenKeys.add(key);
      enabledUrls.push(url);
    });
    return enabledUrls;
  };
  const rerunGroup = async (group) => {
    const enabledUrls = getEnabledUrls(group);
    if (enabledUrls.length === 0) {
      alert('At least one image must be enabled to re-run analysis.');
      return;
    }
    const groupKey = getGroupKey(group);
    setRerunningGroup(groupKey);
    try {
      const response = await axios.post('/analyze', {
        image_urls: enabledUrls,
        resize_mode: group.resize_mode || 'original',
        compression_mode: group.compression_mode || 'none',
        model: group.model_name,
      });
      if (response.data.status === 'success') {
        await refreshHistory();
      } else {
        alert(response.data.message || 'Re-run failed.');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.message || error.message || 'Re-run failed.';
      alert(`Error re-running analysis: ${msg}`);
    } finally {
      setRerunningGroup(null);
    }
  };
  const searchEbayForGroup = async (group) => {
    const groupKey = getGroupKey(group);
    setEbaySearchingGroup(groupKey);
    try {
      const itemIds = group.items.filter((i) => i.id).map((i) => i.id);
      const response = await axios.post('/ebay_search', {
        blob_urls: group.blob_urls || [group.blob_url],
        item_ids: itemIds,
      });
      if (response.data.status === 'success') {
        setEbayResultsMap((prev) => ({ ...prev, [groupKey]: response.data.ebay_results }));
        const ebayJson = response.data.ebay_results;
        setItems((prev) => prev.map((item) => (
          itemIds.includes(item.id) ? { ...item, ebay_results: ebayJson } : item
        )));
      }
    } catch (error) {
      console.error('Error searching eBay:', error);
    } finally {
      setEbaySearchingGroup(null);
    }
  };
  const handlePriceChange = (id, value) => {
    setItems((prevItems) => prevItems.map((item) => (
      item.id === id ? { ...item, actual_price: value } : item
    )));
  };
  const handleManualNameChange = (id, value) => {
    setItems((prevItems) => prevItems.map((item) => (
      item.id === id ? { ...item, manual_name: value } : item
    )));
  };
  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      const response = await axios.post('/update_item_name', {
        id,
        item_name: name,
      });
      if (response.data.status === 'success') {
        setOriginalItemsById((prev) => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            manual_name: name,
          },
        }));
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error updating item name:', error);
      alert('Error updating item name. Please try again.');
    } finally {
      setUpdatingNameId(null);
    }
  };
  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      const response = await axios.post('/update_price', {
        id,
        actual_price: parseFloat(price),
      });
      if (response.data.status === 'success') {
        const parsedPrice = parseFloat(price);
        setOriginalItemsById((prev) => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            actual_price: Number.isFinite(parsedPrice) ? parsedPrice : null,
          },
        }));
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Error updating price. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };
  const groupedItems = useMemo(() => groupHistoryItems(items), [items]);
  const totalPages = Math.max(1, Math.ceil(groupedItems.length / itemsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = groupedItems.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => {
    const nextPage = Math.max(1, Math.min(totalPages, pageNumber));
    setCurrentPage(nextPage);
  };
  const handleNextPage = async () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      return;
    }
    if (!hasMoreHistory || loadingMore) return;
    await fetchItems({ reset: false });
    setCurrentPage((prev) => prev + 1);
  };
  const openImageGroup = (url, urls, itemsForModal) => {
    setSelectedImageUrl(url);
    setSelectedImageUrls(urls);
    setSelectedItems(itemsForModal);
  };
  const openItemImage = (item) => {
    setSelectedImageUrl(item.blob_url);
    setSelectedImageUrls(item.blob_urls || [item.blob_url]);
    setSelectedItems(item);
  };
  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 bg-surface rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-8 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-ink">Auction Item History</h1>
        <div className="flex gap-2 sm:gap-4">
          <button
            onClick={refreshHistory}
            className="flex items-center px-3 sm:px-4 py-2 bg-surface-2 text-ink rounded hover:bg-hairline transition-colors text-sm sm:text-base"
          >
            <RefreshCcw size={16} className={`mr-1.5 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={flushHistory}
            disabled={items.length === 0}
            className={`flex items-center px-3 sm:px-4 py-2 rounded text-white transition-colors text-sm sm:text-base ${
              items.length === 0 ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
            }`}
            title="Clear all analysis history"
          >
            <Trash2 size={16} className="mr-1.5 sm:mr-2" />
            Flush
          </button>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-8">
        {currentGroups.map((group) => (
          <HistoryGroupCard
            key={getGroupKey(group)}
            group={group}
            currentUsername={currentUser?.username}
            disabledImages={disabledImages}
            rerunningGroup={rerunningGroup}
            ebaySearchingGroup={ebaySearchingGroup}
            ebayResultsMap={ebayResultsMap}
            originalItemsById={originalItemsById}
            updatingId={updatingId}
            updatingNameId={updatingNameId}
            onOpenImageGroup={openImageGroup}
            onOpenItemImage={openItemImage}
            onToggleImage={toggleImage}
            onRerunGroup={rerunGroup}
            onSearchEbay={searchEbayForGroup}
            onManualNameChange={handleManualNameChange}
            onUpdateItemName={updateItemName}
            onPriceChange={handlePriceChange}
            onUpdatePrice={updatePrice}
          />
        ))}
        {items.length === 0 && !loading && (
          <div className="p-12 text-center text-muted border-2 border-dashed border-hairline rounded-lg bg-surface-2">
            <p className="italic">No analysis history found.</p>
          </div>
        )}
        <HistoryPagination
          groupedItemsLength={groupedItems.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          hasMoreHistory={hasMoreHistory}
          loadingMore={loadingMore}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          onPaginate={paginate}
          onNextPage={handleNextPage}
        />
      </div>
      <ImageModal
        isOpen={!!selectedItems}
        onClose={() => {
          setSelectedItems(null);
          setSelectedImageUrl(null);
          setSelectedImageUrls(null);
        }}
        imageUrl={selectedImageUrl}
        imageUrls={selectedImageUrls}
        items={selectedItems}
      />
    </div>
  );
};

export default HistoryPage;
